import * as KeyMap from '@packages/utilities/key-map';
import {
  Type,
  EvalEnv,
  Expressions,
  Plugins,
  Component,
  withSystem,
} from '@packages/systems/wfdl';
import {func} from '@packages/utilities/wfdl-helpers';
import {Expression, TRecord, TEmptyRecord} from '@packages/systems/wfdl-ast';
import {weakMemo} from '@packages/utilities/memo';
import {QualifiedMap, qualify, getName} from '@packages/systems/wfdl-Qualified';
import {
  ComponentLocalization,
  ComponentLocalizationDefinition,
  ComponentLocalizationValues,
  ComponentLocalizationMap,
  LocalizationValue,
  LocaleScopes,
  LocalizationView,
  LocalizationKeyDefinition,
} from './types';
import {emptyObject} from '@packages/utilities/fp/utils';
import omit from 'lodash/omit';

// Defined inline to avoid a circular dependency through @packages/utilities/platform.
const pageComponentType = qualify(
  Plugins.name('__SitePlugin'),
  Component.name('page')
);

const EMPTY_DEFINITION: ComponentLocalizationDefinition = {
  keys: emptyObject,
};

export function emptyDefinition(): ComponentLocalizationDefinition {
  return EMPTY_DEFINITION;
}

export function empty(): ComponentLocalization {
  return {definition: EMPTY_DEFINITION, values: emptyObject};
}

export function emptyMap() {
  return QualifiedMap.empty<Component.ComponentName, ComponentLocalization>();
}

export function forEach(
  component: ComponentLocalization,
  fn: (key: string, view: LocalizationView) => void
): void {
  const {keys, deletedAt} = component.definition;
  if (deletedAt) return;

  for (const key in keys) {
    const definition = keys[key]!;
    if (definition.deletedAt) continue;
    const value = component.values[key];
    fn(key, {...definition, ...value});
  }
}

export function getLocalization(
  component: ComponentLocalization,
  key: string,
  {includeDeleted = false}: {includeDeleted?: boolean} = {}
): LocalizationView | undefined {
  const {keys, deletedAt} = component.definition;
  if (deletedAt && !includeDeleted) return;

  const definition = keys[key];
  if (!definition || (definition.deletedAt && !includeDeleted)) return;

  const value = component.values[key];

  return {
    ...definition,
    ...value,
  };
}

export function getDataType(localization: LocalizationView): Type {
  return localization.dataType;
}

export function getValue(
  localization: LocalizationValue
): Expression | undefined {
  return localization.value;
}

export function getValueWithFallback(
  localization: LocalizationView
): Expression {
  return localization.value ?? localization.defaultValue;
}

export function setValue(
  localization: LocalizationView,
  value: Expression | undefined
): LocalizationView {
  return {...localization, value};
}

export function getDefaultValue(localization: LocalizationView): Expression {
  return localization.defaultValue;
}

export function setDefaultValue(
  localization: LocalizationView,
  defaultValue: Expression
): LocalizationView {
  return {...localization, defaultValue};
}

export function getLocaleScopes(localization: LocalizationView) {
  return localization.localeScopes;
}

export function setLocaleScopes(
  localization: LocalizationView,
  localeScopes: LocaleScopes
): LocalizationView {
  if (localeScopes === localization.localeScopes) return localization;
  return {...localization, localeScopes};
}

export function updateLocalization(
  component: ComponentLocalization,
  key: string,
  updateFn: (localization: LocalizationView) => LocalizationView
): ComponentLocalization {
  const view = getLocalization(component, key);
  if (!view) return component;

  const result = updateFn(view);

  const {definition, values} = component;
  let nextDefinitions = definition.keys;
  if (
    result.dataType !== view.dataType ||
    result.defaultValue !== view.defaultValue ||
    result.localeScopes !== view.localeScopes
  ) {
    nextDefinitions = {
      ...nextDefinitions,
      [key]: {
        dataType: result.dataType,
        defaultValue: result.defaultValue,
        localeScopes: result.localeScopes,
      },
    };
  }

  let nextValues = values;
  if (result.value !== view.value) {
    nextValues = {
      ...nextValues,
      [key]: {
        value: result.value,
      },
    };
  }

  return {
    definition:
      nextDefinitions === definition.keys
        ? definition
        : {...definition, keys: nextDefinitions},
    values: nextValues,
  };
}

export function createKey(
  component: ComponentLocalization,
  key: string,
  dataType: Type,
  defaultValue: Expression,
  localeScopes: LocaleScopes
): ComponentLocalization {
  return {
    ...component,
    definition: {
      ...component.definition,
      keys: {
        ...component.definition.keys,
        [key]: {
          dataType,
          defaultValue,
          localeScopes,
        },
      },
    },
  };
}

export function deleteKeys(
  component: ComponentLocalization,
  keys: string[]
): ComponentLocalization {
  return keys.reduce((nextComponent, key) => {
    const removeKey = KeyMap.dissoc(key);
    const nextDefinitions = removeKey(nextComponent.definition.keys);
    const nextValues = removeKey(nextComponent.values);

    if (
      nextDefinitions === nextComponent.definition.keys &&
      nextValues === nextComponent.values
    ) {
      return nextComponent;
    }

    return {
      ...nextComponent,
      definition:
        nextDefinitions === nextComponent.definition.keys
          ? nextComponent.definition
          : {...nextComponent.definition, keys: nextDefinitions},
      values: nextValues,
    };
  }, component);
}

export function removeValues(
  component: ComponentLocalization,
  keys: string[]
): ComponentLocalization {
  const nextValues = keys.reduce(
    (values, key) => KeyMap.dissoc(key)(values),
    component.values
  );
  if (nextValues === component.values) return component;
  return {...component, values: nextValues};
}

export function softDeleteKey(
  component: ComponentLocalization,
  key: string,
  deletedAt: Date
): ComponentLocalization {
  const {definition} = component;
  const keyDefinition = definition.keys[key];
  if (!keyDefinition || keyDefinition.deletedAt) return component;

  return {
    ...component,
    definition: {
      ...definition,
      keys: {
        ...definition.keys,
        [key]: {...keyDefinition, deletedAt},
      },
    },
  };
}

export function restoreKey(
  component: ComponentLocalization,
  key: string,
  definition: LocalizationKeyDefinition
): ComponentLocalization {
  const componentDefinition = component.definition;
  const existing = componentDefinition.keys[key];
  if (existing && !existing.deletedAt) return component;

  return {
    ...component,
    definition: {
      ...componentDefinition,
      keys: {
        ...componentDefinition.keys,
        [key]: omit(existing ?? definition, 'deletedAt'),
      },
    },
  };
}

export function softDeleteComponent(
  component: ComponentLocalization,
  deletedAt: Date
): ComponentLocalization {
  if (component.definition.deletedAt) return component;
  return {
    ...component,
    definition: {...component.definition, deletedAt},
  };
}

export function restoreComponent(
  component: ComponentLocalization | undefined,
  definition: ComponentLocalizationDefinition
): ComponentLocalization {
  if (!component) {
    return {definition: omit(definition, 'deletedAt'), values: emptyObject};
  }
  if (!component.definition.deletedAt) return component;
  return {
    ...component,
    definition: omit(component.definition, 'deletedAt'),
  };
}

const buildComponentLocalizationType = weakMemo(
  (definition: ComponentLocalizationDefinition): Type => {
    if (definition.deletedAt) return TEmptyRecord;

    const keyFields: Record<string, Type> = {};
    let hasField = false;
    for (const key in definition.keys) {
      const keyDefinition = definition.keys[key];
      if (keyDefinition && !keyDefinition.deletedAt) {
        keyFields[key] = keyDefinition.dataType;
        hasField = true;
      }
    }
    return hasField ? TRecord(keyFields) : TEmptyRecord;
  }
);

export const buildLocalizationContextType = weakMemo(
  (map: ComponentLocalizationMap): Type => {
    const componentFields: Record<string, Type> = {};
    let hasComponent = false;
    map.forEach((component, qualifiedName) => {
      if (component.definition.deletedAt) return;
      const compType = buildComponentLocalizationType(component.definition);
      if (compType) {
        componentFields[getName(qualifiedName)] = compType;
        hasComponent = true;
      }
    });
    const type = hasComponent ? TRecord(componentFields) : TEmptyRecord;
    return withSystem({hiddenProperty: true}, type);
  }
);

const binaryUnion = (acc: KeyMap.Type<unknown>, scope: KeyMap.Type<unknown>) =>
  KeyMap.union(acc)(scope);

export function evaluate(
  component: ComponentLocalization,
  env: EvalEnv.EvalEnvType
): Record<string, unknown> {
  const {keys, deletedAt} = component.definition;
  if (deletedAt) return {};

  const result: Record<string, unknown> = {};
  for (const key in keys) {
    const {defaultValue, localeScopes, deletedAt: keyDeletedAt} = keys[key]!;
    if (keyDeletedAt) continue;
    const freeVars = Expressions.findFreeVariables(defaultValue);
    const params = KeyMap.values(localeScopes).reduce(binaryUnion, freeVars);
    const value = component.values[key]?.value ?? defaultValue;
    const expr = func(Object.keys(params).sort(), value);
    result[key] = Expressions.evaluate(env, expr);
  }
  return result;
}

export function createDefinition(
  keys: Record<string, LocalizationKeyDefinition>,
  deletedAt?: Date
): ComponentLocalizationDefinition {
  return deletedAt ? {keys, deletedAt} : {keys};
}

export function withValues(
  definition: ComponentLocalizationDefinition,
  values: ComponentLocalizationValues
): ComponentLocalization {
  return {definition, values};
}

export function splitValues(
  component: ComponentLocalization
): [ComponentLocalizationDefinition, ComponentLocalizationValues] {
  return [component.definition, component.values];
}

type RawComponentDefinition = {
  componentId: string;
  definition: ComponentLocalizationDefinition;
};

type RawValuesEntry = {
  componentId: string;
  map: ComponentLocalizationValues;
};

/**
 * Builds a ComponentLocalizationMap from page and component definitions and values.
 */
export function reifyMap(rawLocalizations: {
  pageDefinition?: ComponentLocalizationDefinition;
  pageValues?: {map: ComponentLocalizationValues};
  componentDefinitions: RawComponentDefinition[];
  componentValues: RawValuesEntry[];
}): ComponentLocalizationMap {
  const mutable = QualifiedMap.empty<
    Component.ComponentName,
    ComponentLocalization
  >().asMutableCopy();

  const valuesIndex = new Map(
    rawLocalizations.componentValues.map((v) => [v.componentId, v.map])
  );

  for (const defDoc of rawLocalizations.componentDefinitions) {
    const key = qualify(
      Plugins.name('__SitePlugin'),
      Component.name(defDoc.componentId)
    );
    mutable.set_mut(
      key,
      withValues(
        defDoc.definition,
        valuesIndex.get(defDoc.componentId) ?? emptyObject
      )
    );
  }

  if (rawLocalizations.pageDefinition) {
    mutable.set_mut(
      pageComponentType,
      withValues(
        rawLocalizations.pageDefinition,
        rawLocalizations.pageValues?.map ?? emptyObject
      )
    );
  }

  return mutable.asNonMutableCopy();
}

/**
 * Returns a new map where every component's localization values are replaced with the
 * matching entry from `componentValues`, keyed by componentId.
 */
export function reifyMapValues(
  map: ComponentLocalizationMap,
  componentValues: RawValuesEntry[]
): ComponentLocalizationMap {
  const valuesIndex = new Map(
    componentValues.map((v) => [v.componentId, v.map])
  );

  return map.map((loc, componentName) => {
    const [definition] = splitValues(loc);
    return withValues(
      definition,
      valuesIndex.get(getName(componentName)) ?? emptyObject
    );
  });
}

export function reify(
  dataTypes: Record<string, Type>,
  values: Record<string, Expression | undefined>,
  defaultValues: Record<string, Expression>,
  localeScopess: Record<string, LocaleScopes>
): ComponentLocalization {
  const reifiedDefinitions: ComponentLocalizationDefinition['keys'] = {};
  const reifiedValues: ComponentLocalizationValues = {};

  for (const key in dataTypes) {
    const dataType = dataTypes[key]!;
    const localeScopes = localeScopess[key] ?? KeyMap.empty();

    // Default values are required to have a valid localization.
    const defaultValue = defaultValues[key];
    if (!defaultValue) {
      // TODO: log here
      continue;
    }

    const value = values[key];

    reifiedDefinitions[key] = {
      dataType,
      defaultValue,
      localeScopes,
    };
    if (value !== undefined) {
      reifiedValues[key] = {value};
    }
  }

  return {
    definition: {keys: reifiedDefinitions},
    values: reifiedValues,
  };
}
